import {
  PLAY_STORE_LIVE,
  IOS_APP_STORE_LIVE,
  liveAppStores,
  type AppStoreStatus,
} from "@shared/appStore";

const SHIPPED: AppStoreStatus = { play: PLAY_STORE_LIVE, ios: IOS_APP_STORE_LIVE };

/**
 * Runtime <head> for the lamirage.co ROOT only (spec 2026-10-07 §3).
 *
 * client/index.html is the shell for every host the SPA serves (lamirage.co, couple wedding
 * sites, lamirage.live, pics, the Capacitor app), so it carries only neutral venue facts.
 * App-promotional title/description and the MobileApplication JSON-LD are set here, by
 * home.tsx, and only when location.pathname === "/" — App.tsx also renders <Home/> for the
 * catch-all route, and unmatched paths must keep the neutral head. Every change is undone by
 * the returned restore function (called on unmount).
 *
 * Deliberately NO apple-itunes-app meta: it would put a download banner on guests' live pages.
 */

export const LANDING_TITLE =
  "LA Mirage — India's first wedding venue with its own app | Koratty, Kerala";
export const LANDING_DESCRIPTION =
  "India's first wedding venue with its own app, as far as we know: plan, invite, stream and relive your wedding at LA Mirage Koratty, Kerala.";

const JSONLD_ID = "lm-landing-mobileapp-jsonld";

/**
 * The MobileApplication JSON-LD, or null while no store lists the app. operatingSystem and
 * downloadUrl name ONLY the live stores (today: Android / Google Play).
 */
export function mobileAppJsonLd(stores: AppStoreStatus = SHIPPED): Record<string, unknown> | null {
  const live = liveAppStores(stores);
  if (live.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "MobileApplication",
    name: "LA Mirage",
    operatingSystem: live.map((s) => s.os).join(", "),
    applicationCategory: "LifestyleApplication",
    downloadUrl: live.map((s) => s.url),
    offers: { "@type": "Offer", price: "0", priceCurrency: "INR" },
    publisher: {
      "@type": "Organization",
      name: "LA Mirage Koratty",
      url: "https://la-mirage.in/",
    },
  };
}

/** Minimal DOM surface this needs — lets the unit test run in node without jsdom. */
export interface HeadDoc {
  title: string;
  head: {
    querySelector(sel: string): HeadEl | null;
    appendChild(el: HeadEl): unknown;
  };
  createElement(tag: string): HeadEl;
}
export interface HeadEl {
  getAttribute(name: string): string | null;
  setAttribute(name: string, value: string): void;
  remove(): void;
  textContent: string | null;
  id?: string;
}

const META: Array<{ attr: "name" | "property"; key: string; value: string }> = [
  { attr: "name", key: "description", value: LANDING_DESCRIPTION },
  { attr: "property", key: "og:title", value: LANDING_TITLE },
  { attr: "property", key: "og:description", value: LANDING_DESCRIPTION },
  { attr: "name", key: "twitter:title", value: LANDING_TITLE },
  { attr: "name", key: "twitter:description", value: LANDING_DESCRIPTION },
];

/**
 * Apply the landing head. Returns a restore function that puts back the previous title and
 * meta contents, removes metas it had to create, and removes the JSON-LD script.
 * When pathname is not "/" it changes nothing and returns a no-op.
 */
export function applyLandingHead(
  doc: HeadDoc,
  pathname: string,
  stores: AppStoreStatus = SHIPPED,
): () => void {
  if (pathname !== "/") return () => {};

  const undo: Array<() => void> = [];

  const prevTitle = doc.title;
  doc.title = LANDING_TITLE;
  undo.push(() => {
    doc.title = prevTitle;
  });

  for (const m of META) {
    const sel = `meta[${m.attr}="${m.key}"]`;
    const existing = doc.head.querySelector(sel);
    if (existing) {
      const prev = existing.getAttribute("content");
      existing.setAttribute("content", m.value);
      undo.push(() => {
        if (prev === null) existing.setAttribute("content", "");
        else existing.setAttribute("content", prev);
      });
    } else {
      const el = doc.createElement("meta");
      el.setAttribute(m.attr, m.key);
      el.setAttribute("content", m.value);
      doc.head.appendChild(el);
      undo.push(() => el.remove());
    }
  }

  const ld = mobileAppJsonLd(stores);
  if (ld && !doc.head.querySelector(`script#${JSONLD_ID}`)) {
    const s = doc.createElement("script");
    s.setAttribute("type", "application/ld+json");
    s.setAttribute("id", JSONLD_ID);
    s.textContent = JSON.stringify(ld);
    doc.head.appendChild(s);
    undo.push(() => s.remove());
  }

  return () => {
    for (let i = undo.length - 1; i >= 0; i--) undo[i]();
  };
}
